import React, { useEffect, useMemo, useState } from 'react';
import { normalizeGiphyRating, searchGiphy } from '../../utils/giphy';

export default function GifPicker({
  open,
  rating = 'g',
  onSelect,
  onClose,
  disabled = false,
  surface = 'boards',
}) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const effectiveRating = useMemo(() => normalizeGiphyRating(rating, 'g'), [rating]);

  useEffect(() => {
    if (!open || disabled) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError('');
      searchGiphy({ query, rating: effectiveRating, surface })
        .then((nextItems) => {
          if (!cancelled) setItems(nextItems);
        })
        .catch((err) => {
          if (cancelled) return;
          setItems([]);
          setError(err?.message || 'Could not load GIFs.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, query.trim() ? 220 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [disabled, effectiveRating, open, query, surface]);

  if (!open) return null;

  return (
    <div className="gif-picker" role="dialog" aria-label="Choose a GIF">
      <div className="gif-picker__header">
        <label className="gif-picker__search">
          <span className="sr-only">Search GIFs</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search GIPHY"
            autoFocus
            disabled={disabled}
          />
        </label>
        <button type="button" className="gif-picker__close" onClick={onClose} aria-label="Close GIF picker">×</button>
      </div>

      {disabled ? (
        <p className="gif-picker__empty">GIFs are disabled here.</p>
      ) : loading ? (
        <p className="gif-picker__empty">Loading GIFs...</p>
      ) : error ? (
        <p className="gif-picker__empty">{error}</p>
      ) : items.length === 0 ? (
        <p className="gif-picker__empty">No GIFs found.</p>
      ) : (
        <div className="gif-picker__grid">
          {items.map((gif) => (
            <button
              type="button"
              key={gif.providerId}
              className="gif-picker__item"
              onClick={() => onSelect?.(gif)}
              title={gif.title}
            >
              <img src={gif.previewUrl || gif.url} alt={gif.title || 'GIF'} loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <div className="gif-picker__attribution">Powered by GIPHY</div>
    </div>
  );
}
